// src/pages/InventoryDesktop.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardShell from "../components/DashboardShell";
import { api } from "../scripts/api";
import CardComponent from "../components/CardComponent";
import NotesWindow from "../components/NotesWindow";
import { RefreshCw, MoreVertical, X, Save } from "lucide-react";
import { STATUS_BY_CODE } from "../scripts/status";

const SORT_KEYS = [
  { k: "unit_status", label: "Status" },
  { k: "unit_category", label: "Category" },
  { k: "color_code", label: "Status" },
  { k: "rowo", label: "RO/WO" },
  { k: "stock_po", label: "Stock PO" },
  { k: "customer", label: "Customer" },
];

const CATEGORY_OPTIONS = [
  { v: "", label: "(blank)" },
  { v: "landfill", label: "Landfill" },
  { v: "sewer", label: "Sewer equipment" },
  { v: "sweeper", label: "Sweeper" },
  { v: "refuse", label: "Refuse" },
  { v: "municipal", label: "Municipal" },
  { v: "mrr", label: "MRR" },
  { v: "rail", label: "rail" },
];

const INVENTORY_CATEGORIES = [
  { key: "all", label: "All Inventory" },
  { key: "demo", label: "Demo" },
  { key: "stock", label: "Stock" },
  { key: "rental", label: "Rental" },
  { key: "sold", label: "Sold" },
];

function norm(v) {
  return String(v ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}
function buildSearchIndex(row) {
  const parts = [];
  for (const v of Object.values(row || {})) {
    if (v == null) continue;
    if (typeof v === "object") continue;
    const s = norm(v);
    if (s) parts.push(s);
  }
  return parts.join(" | ");
}
function valStr(v) {
  if (v === null || v === undefined) return "";
  return String(v).toLowerCase();
}
function getCustomerName(r) {
  return (r.customer || "").toString().toLowerCase();
}
function isTruthyFlag(v) {
  const s = String(v ?? "").toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function StatMiniSmall({ title, value, sub }) {
  return (
    <div className="scan-tile" style={{ textAlign: "left", padding: 10, minHeight: 0 }}>
      <div className="scan-title" style={{ fontSize: 11, opacity: 0.8, letterSpacing: 0.2 }}>
        {title}
      </div>
      <div style={{ fontSize: 16, fontWeight: 900, marginTop: 4, lineHeight: 1.1 }}>{value}</div>
      {sub ? (
        <div className="scan-sub" style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function HoverActions({ onMoveUnit, onOther1, onOther2 }) {
  return (
    <div
      className="inv-hover-actions"
      style={{
        position: "absolute",
        top: 10,
        right: 10,
        display: "none",
        alignItems: "center",
        gap: 6,
        padding: 6,
        borderRadius: 12,
        background: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(6px)",
        zIndex: 5,
      }}
    >
      <button className="btn-outline" style={{ padding: "6px 10px" }} onClick={onMoveUnit} type="button">
        Move Unit
      </button>
      <button className="btn-outline" style={{ padding: "6px 10px" }} onClick={onOther1} type="button">
        Other
      </button>
      <button className="btn-outline" style={{ padding: "6px 10px" }} onClick={onOther2} type="button">
        Other
      </button>
      <span style={{ opacity: 0.7, paddingLeft: 2 }}>
        <MoreVertical size={16} />
      </span>
    </div>
  );
}

function AddNoteModal({ open, inventoryId, onClose, onSaved }) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setBody("");
    setErr("");
    setSaving(false);
  }, [open, inventoryId]);

  if (!open) return null;

  const save = async () => {
    const text = String(body || "").trim();
    if (!text) {
      setErr("Enter a note.");
      return;
    }

    setSaving(true);
    setErr("");
    try {
      await api.post(`/inventory/${inventoryId}/notes`, { body: text });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.response?.data?.message || "Failed to save note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 9999 }}
    >
      <div
        className="modal"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(640px, 92vw)",
          maxHeight: "86vh",
          overflow: "hidden",
          padding: 0,
        }}
      >
        <div
          style={{
            padding: 14,
            borderBottom: "1px solid var(--card-border)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 950, fontSize: 16, color: "var(--fg)" }}>Add Note</div>
            <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>Inventory #{inventoryId}</div>
          </div>

          <button className="btn-outline" onClick={onClose} type="button" title="Close" disabled={saving}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 14 }}>
          <textarea
            className="input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder="Write a note…"
            style={{ width: "100%", resize: "vertical", fontSize: 14, lineHeight: 1.35 }}
            disabled={saving}
          />

          {err ? (
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)", whiteSpace: "pre-wrap" }}>{err}</div>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
            <button className="btn-outline" onClick={onClose} type="button" disabled={saving}>
              Cancel
            </button>
            <button className="btn" onClick={save} type="button" disabled={saving}>
              <Save size={16} style={{ marginRight: 8 }} />
              {saving ? "Saving…" : "Save Note"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InventoryDesktop() {
  const table = "all_inventory";

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(() => localStorage.getItem("inv_q") || "");
  const [cat, setCat] = useState(() => localStorage.getItem("inv_cat") || "__all__");
  const [sortKey, setSortKey] = useState(() => localStorage.getItem("inv_sort_key") || "color_code");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("inv_sort_dir") || "asc");

  const [colorFilter, setColorFilter] = useState(() => localStorage.getItem("inv_color_filter") || "");
  const [vendorFilter, setVendorFilter] = useState(() => localStorage.getItem("inv_vendor_filter") || "");
  const [filterChassis, setFilterChassis] = useState(() => localStorage.getItem("inv_filter_chassis") === "true");
  const [filterBody, setFilterBody] = useState(() => localStorage.getItem("inv_filter_body") === "true");
  const [filterAttachment, setFilterAttachment] = useState(() => localStorage.getItem("inv_filter_attachment") === "true");
  const [filterEquipment, setFilterEquipment] = useState(() => localStorage.getItem("inv_filter_equipment") === "true");

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [summary, setSummary] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");

  // notes summary: { [id]: { total, unread } }
  const [noteSummary, setNoteSummary] = useState({});
  const notesDebounceRef = useRef(null);

  // Add note modal state
  const [addNoteForId, setAddNoteForId] = useState(null);

  // Notes window state
  const [notesOpenId, setNotesOpenId] = useState(null);
  const [notesTitle, setNotesTitle] = useState("");

  useEffect(() => localStorage.setItem("inv_cat", cat), [cat]);
  useEffect(() => localStorage.setItem("inv_q", q), [q]);
  useEffect(() => {
    localStorage.setItem("inv_sort_key", sortKey);
    localStorage.setItem("inv_sort_dir", sortDir);
  }, [sortKey, sortDir]);
  useEffect(() => localStorage.setItem("inv_color_filter", colorFilter), [colorFilter]);
  useEffect(() => localStorage.setItem("inv_vendor_filter", vendorFilter), [vendorFilter]);
  useEffect(() => localStorage.setItem("inv_filter_chassis", String(filterChassis)), [filterChassis]);
  useEffect(() => localStorage.setItem("inv_filter_body", String(filterBody)), [filterBody]);
  useEffect(() => localStorage.setItem("inv_filter_attachment", String(filterAttachment)), [filterAttachment]);
  useEffect(() => localStorage.setItem("inv_filter_equipment", String(filterEquipment)), [filterEquipment]);

  const load = async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const res = await api.get(`/${table}`);
      const data = res?.data;
      const list = Array.isArray(data) ? data : [];
      setRows(list);
    } catch (e) {
      console.error("Load failed:", e);
      setRows([]);
      setErrMsg(e?.response?.data?.error || "Failed to load inventory.");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const { data } = await api.get(`/reports/summary`, { params: { table } });
      setSummary(data || null);
    } catch {
      setSummary(null);
    }
  };

  useEffect(() => {
    load();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uniqueColors = useMemo(() => {
    const set = new Set(rows.map((r) => String(r.color_code ?? "")));
    return [...set].sort((a, b) => Number(a) - Number(b));
  }, [rows]);

  const statusOptions = useMemo(() => {
    return uniqueColors.map((c) => ({
      v: c,
      label: c === "" ? "(blank)" : STATUS_BY_CODE[Number(c)]?.label || `Unknown (${c})`,
    }));
  }, [uniqueColors]);

  const rowsIndexed = useMemo(() => rows.map((r) => ({ ...r, __idx: buildSearchIndex(r) })), [rows]);

  const filtered = useMemo(() => {
    const terms = norm(q).split(" ").filter(Boolean);
    let arr = rowsIndexed;

    if (cat !== "__all__") arr = arr.filter((r) => norm(r.unit_category) === norm(cat));

    if (activeCategory !== "all") {
      if (activeCategory === "demo") arr = arr.filter((r) => isTruthyFlag(r.is_demo));
      else if (activeCategory === "stock") arr = arr.filter((r) => isTruthyFlag(r.is_stock));
      else if (activeCategory === "rental") arr = arr.filter((r) => isTruthyFlag(r.is_rental));
      else if (activeCategory === "sold") arr = arr.filter((r) => norm(r.unit_status) === "sold");
    }

    if (colorFilter !== "") arr = arr.filter((r) => String(r.color_code ?? "") === colorFilter);

    if (vendorFilter) arr = arr.filter((r) => norm(r.description || r.desc || "").includes(norm(vendorFilter)));

    const hasTypeFilter = filterChassis || filterBody || filterAttachment || filterEquipment;
    if (hasTypeFilter) {
      arr = arr.filter((r) =>
        (filterChassis && isTruthyFlag(r.is_chassis)) ||
        (filterBody && isTruthyFlag(r.is_body)) ||
        (filterAttachment && isTruthyFlag(r.is_attachment)) ||
        (filterEquipment && isTruthyFlag(r.is_equipment))
      );
    }

    if (terms.length) {
      arr = arr.filter((r) => {
        const hay = r.__idx || "";
        for (const t of terms) if (!hay.includes(t)) return false;
        return true;
      });
    }

    const dir = sortDir === "desc" ? -1 : 1;

    return [...arr].sort((a, b) => {
      let va = "";
      let vb = "";

      if (sortKey === "customer") {
        va = getCustomerName(a);
        vb = getCustomerName(b);
      } else if (sortKey === "color_code") {
        va = Number(a.color_code ?? 0);
        vb = Number(b.color_code ?? 0);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      } else {
        va = valStr(a[sortKey]);
        vb = valStr(b[sortKey]);
      }

      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [rowsIndexed, q, cat, sortKey, sortDir, activeCategory, colorFilter, vendorFilter, filterChassis, filterBody, filterAttachment, filterEquipment]);

  const refreshNotesSummary = async (list) => {
    const ids = (list || [])
      .map((r) => r.id)
      .filter((x) => x != null)
      .slice(0, 600);

    if (!ids.length) {
      setNoteSummary({});
      return;
    }

    try {
      const { data } = await api.get("/inventory/notes/summary", { params: { ids: ids.join(",") } });
      setNoteSummary(data?.byId || {});
    } catch {
      // don't block the page if notes fails
      setNoteSummary({});
    }
  };

  // refresh notes summary when filtered changes (debounced)
  useEffect(() => {
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current);
    notesDebounceRef.current = setTimeout(() => {
      refreshNotesSummary(filtered);
    }, 250);

    return () => notesDebounceRef.current && clearTimeout(notesDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  const filterContent = (
    <div>
      <div className="dash-section">Search</div>
      <input
        className="input"
        placeholder="Search…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ width: "100%", marginTop: 8 }}
      />

      <div className="dash-section">Category</div>
      <select className="input" style={{ width: "100%", marginTop: 8 }} value={cat} onChange={(e) => setCat(e.target.value)}>
        <option value="__all__">All categories</option>
        {CATEGORY_OPTIONS.map((o) => (
          <option key={o.v} value={o.v}>
            {o.label}
          </option>
        ))}
      </select>

      <div className="dash-section">Status</div>
      <select
        className="input"
        style={{ width: "100%", marginTop: 8 }}
        value={colorFilter}
        onChange={(e) => setColorFilter(e.target.value)}
      >
        <option value="">All statuses</option>
        {statusOptions.map((o) => (
          <option key={o.v} value={o.v}>
            {o.label}
          </option>
        ))}
      </select>

      <div className="dash-section">Vendor (stub)</div>
      <input
        className="input"
        placeholder="Filter by vendor…"
        value={vendorFilter}
        onChange={(e) => setVendorFilter(e.target.value)}
        style={{ width: "100%", marginTop: 8 }}
      />

      <div className="dash-section">Unit Types</div>
      <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={filterChassis} onChange={(e) => setFilterChassis(e.target.checked)} />
          Chassis
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={filterBody} onChange={(e) => setFilterBody(e.target.checked)} />
          Body
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={filterAttachment} onChange={(e) => setFilterAttachment(e.target.checked)} />
          Attachment
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={filterEquipment} onChange={(e) => setFilterEquipment(e.target.checked)} />
          Equipment
        </label>
      </div>

      <div className="dash-section">Inventory Type</div>
      <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
        {INVENTORY_CATEGORIES.map(({ key, label }) => (
          <button
            key={key}
            className={activeCategory === key ? "btn" : "btn-outline"}
            style={{ width: "100%", justifyContent: "flex-start" }}
            onClick={() => setActiveCategory(key)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="dash-section">Sort</div>
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        <select className="input" style={{ width: "100%" }} value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
          {SORT_KEYS.map((s) => (
            <option key={s.k} value={s.k}>
              {s.label}
            </option>
          ))}
        </select>
        <select className="input" style={{ width: "100%" }} value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
          <option value="asc">Asc</option>
          <option value="desc">Desc</option>
        </select>
      </div>

      <div className="dash-section">Actions</div>
      <button
        className="btn-outline"
        style={{ width: "100%", marginTop: 8 }}
        onClick={() => {
          load();
          loadSummary();
          refreshNotesSummary(filtered);
        }}
        disabled={loading}
        type="button"
      >
        <RefreshCw size={16} style={{ marginRight: 8 }} />
        Refresh
      </button>

      {errMsg ? (
        <div className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
          {errMsg}
        </div>
      ) : null}
    </div>
  );

  const top = (
    <div
      className="scan-grid"
      style={{
        marginTop: 0,
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 10,
      }}
    >
      <StatMiniSmall title="Total" value={String(summary?.total ?? rows.length)} sub="records" />
      <StatMiniSmall title="Alerts" value={String(summary?.flags?.alerts ?? "-")} sub="flagged" />
      <StatMiniSmall
        title="Stock / Demo / Rental"
        value={
          summary?.flags
            ? `${summary.flags.is_stock ?? 0} / ${summary.flags.is_demo ?? 0} / ${summary.flags.is_rental ?? 0}`
            : "-"
        }
        sub="flags"
      />
      <StatMiniSmall title="Showing" value={String(filtered.length)} sub="filtered" />
    </div>
  );

  const openNotesFor = (r) => {
    const desc =
      r.make_model ||
      r.chassis_make_model ||
      r.body_make_model ||
      r.body_model_size ||
      r.description ||
      r.desc ||
      "";
    const title = `${r.customer || "Unit"} - ${desc || `#${r.id}`}`;
    setNotesTitle(title);
    setNotesOpenId(r.id);
  };

  const openAddNoteFor = (r) => {
    setAddNoteForId(r.id);
  };

  return (
    <div className="container">
      <style>{`
        .inv-card:hover .inv-hover-actions { display: inline-flex; }
      `}</style>

      <DashboardShell left={filterContent} top={top}>
        {loading && <div className="muted">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="muted">No records.</div>}

        <div className="col">
          {filtered.map((r) => {
            const ns = noteSummary?.[r.id] || { total: 0, unread: 0 };

            return (
              <div key={r.id} className="card inv-card" style={{ minWidth: 0, position: "relative" }}>
                <HoverActions
                  onMoveUnit={() => alert(`Move Unit: #${r.id}`)}
                  onOther1={() => alert(`Other action 1: #${r.id}`)}
                  onOther2={() => alert(`Other action 2: #${r.id}`)}
                />

                <CardComponent
                  record={r}
                  table="all_inventory"
                  notesTotal={ns.total ?? 0}
                  notesUnread={ns.unread ?? 0}
                  onOpenNotes={() => openNotesFor(r)}
                  onAddNote={() => openAddNoteFor(r)}
                />
              </div>
            );
          })}
        </div>

        <AddNoteModal
          open={addNoteForId != null}
          inventoryId={addNoteForId ?? 0}
          onClose={() => setAddNoteForId(null)}
          onSaved={() => refreshNotesSummary(filtered)}
        />

        <NotesWindow
          open={notesOpenId != null}
          inventoryId={notesOpenId ?? 0}
          inventoryTitle={notesTitle}
          onClose={() => setNotesOpenId(null)}
          onSeen={() => refreshNotesSummary(filtered)}
        />
      </DashboardShell>
    </div>
  );
}